/* global importName */
// We need to transition to using the account ID globally and this is that step
import appRoutes from './apps/'
import audienceRoutes from './audience'
import billingRoutes from './billing'
import campaignRoutes from './campaigns'
import showRoutes from './shows/'
import store from '@/services/store'
import { getDeepProperty } from '@/helpers/deepObject.js'
import * as types from '@/services/store/modules/account/mutation-types.js'
import redirectRoutes from '../redirects'

const AccountLayout = () => importName('@/scenes/Account/components/AccountLayout.vue', 'accountLayout')
const Delete = () => importName('@/scenes/Account/scenes/Delete/Delete.vue')
const EmailNotifications = () => importName('@/scenes/Account/scenes/EmailNotifications/EmailNotifications.vue')
const UserInvitations = () => importName('@/scenes/Account/scenes/UserInvitations')
const Settings = () => importName('@/scenes/Account/scenes/Settings/Settings.vue', 'accountSettings')
const EditUser = () => importName('@/scenes/Account/scenes/Teams/scenes/Users/scenes/Edit/Edit.vue', 'orgs-users-edit')
const InviteUsers = () => importName('@/scenes/Account/scenes/Teams/scenes/Users/scenes/Invite/Invite.vue', 'orgs-users-invite')
const Invited = () => importName('@/scenes/Account/scenes/Teams/scenes/Users/scenes/Invited/Invited.vue', 'orgs-users-invited')
const Users = () => importName('@/scenes/Account/scenes/Teams/scenes/Users/Users.vue', 'orgs-users')
const TeamsLayout = () => importName('@/scenes/Account/scenes/Teams/components/TeamsLayout.vue', 'orgsLayout')
const Networks = () => importName('@/scenes/Account/scenes/Teams/scenes/Networks/Networks.vue', 'orgs-networks')
const EditNetwork = () => importName('@/scenes/Account/scenes/Teams/scenes/Networks/scenes/EditNetwork.vue', 'orgs-networks-edit')
const EditUserNetworks = () => importName('@/scenes/Account/scenes/Teams/scenes/Users/scenes/Edit/scenes/Networks.vue', 'orgs-users-edit-networks')
const EditUserShows = () => importName('@/scenes/Account/scenes/Teams/scenes/Users/scenes/Edit/scenes/Shows.vue', 'orgs-users-edit-shows')
const NotFound = () => importName('@/scenes/NotFound.vue', 'notFoundAccount')

const accountsRoutes = [
  {
    path: '/accounts/:accountId',
    name: 'accounts',
    component: AccountLayout,
    props: true,
    children: [
      // /accounts/:accountId/edit
      {
        path: 'edit',
        name: 'accounts-settings',
        component: Settings,
        meta: {
          breadcrumbs: [
            { title: 'Settings', to: 'accounts-settings' },
            { title: 'Account Settings' }
          ]
        },
        beforeEnter: async (to, from, next) => {
          // lol we do not want people without proper permissions getting to this. nope nope.
          let currentAccount = store.getters.currentAccount
          let defaultAccount = store.getters.defaultAccount
          // Allow a user with org/network permissions to view the org settings even if they can't edit
          const canView = (
            getDeepProperty(currentAccount, 'permission.can_edit') ||
            (
              getDeepProperty(currentAccount, 'permission.can_edit_teams') &&
              getDeepProperty(currentAccount, 'permission.can_edit_networks')
            )
          )
          if (!canView) {
            next({
              name: 'accounts-settings',
              params: {
                accountId: defaultAccount.id
              }
            })
            return
          }
          next()
        }
      },
      // /accounts/:accountId/email-notifications
      {
        path: 'email-notifications',
        name: 'email-notifications',
        component: EmailNotifications,
        meta: {
          breadcrumbs: [
            { title: 'Settings', to: 'accounts-settings' },
            { title: 'Email Notifications' }
          ]
        }
      },
      {
        path: 'invitations',
        name: 'invitations',
        component: UserInvitations,
        meta: {
          breadcrumbs: [
            { title: 'Settings', to: 'accounts-settings' },
            { title: 'Invitations' }
          ]
        }
      },
      // /accounts/:accountId/delete
      {
        path: 'delete',
        name: 'accounts-delete-account',
        component: Delete,
        meta: {
          breadcrumbs: [
            { title: 'Settings', to: 'accounts-settings' }
          ]
        },
        beforeEnter: async (to, from, next) => {
          // lol we do not want people without proper permissions getting to this. nope nope.
          let currentAccount = store.getters.currentAccount
          let defaultAccount = store.getters.defaultAccount
          const canDeleteAccount = getDeepProperty(currentAccount, 'permission.can_delete')
          if (!canDeleteAccount) {
            next({
              name: 'accounts-settings',
              params: {
                accountId: defaultAccount.id
              }
            })
            return
          }
          next()
        }
      },
      {
        path: 'users',
        component: TeamsLayout,
        props: true,
        children: [
          {
            path: '',
            name: 'orgs-users',
            component: Users,
            props: true,
            meta: {
              breadcrumbs: [
                { title: 'Settings' },
                { title: 'Users' }
              ]
            }
          },
          {
            path: 'invited',
            name: 'orgs-users-invited',
            component: Invited,
            props: true,
            meta: {
              breadcrumbs: [
                { title: 'Settings' },
                { title: 'Users', to: 'orgs-users' },
                { title: 'Invited' }
              ]
            }
          },
          {
            path: 'invite',
            name: 'orgs-users-invite',
            component: InviteUsers,
            props: true,
            meta: {
              breadcrumbs: [
                { title: 'Settings' },
                { title: 'Users', to: 'orgs-users' },
                { title: 'Add a User' }
              ]
            }
          },
          {
            path: ':userId/edit',
            name: 'orgs-users-edit',
            component: EditUser,
            props: true,
            meta: {
              breadcrumbs: [
                { title: 'Settings' },
                { title: 'Users', to: 'orgs-users' },
                { title: 'Edit User' }
              ]
            }
          },
          {
            path: ':userId/edit/networks',
            name: 'orgs-users-edit-networks',
            component: EditUserNetworks,
            props: true,
            meta: {
              breadcrumbs: [
                { title: 'Settings' },
                { title: 'Users', to: 'orgs-users' },
                { title: 'Edit User', to: 'orgs-users-edit' },
                { title: 'Edit Networks' }
              ]
            }
          },
          {
            path: ':userId/edit/shows',
            name: 'orgs-users-edit-shows',
            component: EditUserShows,
            props: true,
            meta: {
              breadcrumbs: [
                { title: 'Settings' },
                { title: 'Users', to: 'orgs-users' },
                { title: 'Edit User', to: 'orgs-users-edit' },
                { title: 'Edit Shows' }
              ]
            }
          }
        ],
        beforeEnter: async (to, from, next) => {
          let currentAccount = store.getters.currentAccount
          let defaultAccount = store.getters.defaultAccount
          const canEditTeams = getDeepProperty(currentAccount, 'permission.can_edit_teams')
          if (!canEditTeams) {
            next({
              name: 'accounts-settings',
              params: {
                accountId: defaultAccount.id
              }
            })
            return
          }
          next()
        }
      },
      {
        path: 'networks',
        component: TeamsLayout,
        props: true,
        children: [
          {
            path: '',
            name: 'orgs-networks',
            component: Networks,
            props: true,
            meta: {
              breadcrumbs: [
                { title: 'Settings' },
                { title: 'Networks' }
              ]
            }
          },
          {
            path: 'new',
            name: 'orgs-networks-new',
            component: EditNetwork,
            props: true,
            meta: {
              breadcrumbs: [
                { title: 'Settings' },
                { title: 'Networks', to: 'orgs-networks' },
                { title: 'Create a Network' }
              ]
            }
          },
          {
            path: ':networkId/edit',
            name: 'orgs-networks-edit',
            component: EditNetwork,
            props: true,
            meta: {
              breadcrumbs: [
                { title: 'Settings' },
                { title: 'Networks', to: 'orgs-networks' },
                { title: 'Edit Network' }
              ]
            }
          }
        ],
        beforeEnter: async (to, from, next) => {
          let currentAccount = store.getters.currentAccount
          let defaultAccount = store.getters.defaultAccount
          const canEditNetworks = getDeepProperty(currentAccount, 'permission.can_edit_networks')
          if (!canEditNetworks) {
            next({
              name: 'accounts-settings',
              params: {
                accountId: defaultAccount.id
              }
            })
            return
          }
          next()
        }
      },

      ...appRoutes,
      ...audienceRoutes,
      ...campaignRoutes,
      ...showRoutes,
      ...billingRoutes,
      ...redirectRoutes,
      {
        path: '*',
        name: 'not-found-account',
        component: NotFound
      }
    ],
    beforeEnter: async (to, from, next) => {
      // Make sure we're loading an account that is legit
      // Right now the Auth Guard loads both the user and all their respective accounts. We should
      // never get here without user and accounts be loaded. If they aren't there's a big problem.

      // We will check the route ID on the user's accounts list and either continue or redirect
      // accordingly. I think I may only want to load the user up front and the let the routes
      // do the additional account loading, but that's for a later day
      let hasAccount = false
      const user = store.getters.user

      user.accounts.collection.map((account) => {
        if (to.params.accountId === account.id) {
          store.commit(types.SET_CURRENT_ACCOUNT, account)
          hasAccount = true
        }
      })

      if (!hasAccount) {
        store.commit(types.SET_CURRENT_ACCOUNT, user.accounts.collection[0])
        to.params.accountId = user.accounts.collection[0].id
        next({name: to.name, params: to.params})
        return
      }

      // The overview page for right now needs to redirect to the podcast ID endpoint
      // at some point we'll have it's own dedicated page that doesn't rely on a
      // podcast being loaded
      let activePodcast = store.getters.activePodcast
      // Podcast loading will always be handled by the time we get here, so it either exists or doesn't
      // and we can redirect appropriately to the proper page
      if (to.name === 'accounts') {
        if (getDeepProperty(activePodcast, 'id')) {
          next({name: 'show-overview', params: { accountId: to.params.accountId, podcastId: getDeepProperty(activePodcast, 'id') }})
        } else {
          next({name: 'new-show', params: { accountId: to.params.accountId}})
        }
      }

      next()
    }
  }
]

export default accountsRoutes
